/*global $*/
import { decodeValue, deparam, setStyle } from '@/lib/utils';
import browserDetect from '@/lib/vendor/browserDetect';
import { lockBodyScroll, unLockBodyScroll } from '@/lib/scroll';

class FullscreenPopup {
  constructor(windowService) {
    this.windowService = windowService;
    this.fullscreenPopups = [];
  }

  loadFullscreenPopup(id) {
    let idx = id.substr(id.lastIndexOf('-') + 1);
    idx = isFinite(idx) ? parseInt(idx) : 0;
    this.loadFullscreenPopupByIdx(idx);
  }

  loadFullscreenPopupByIdx(idx) {
    const focused_popup = this.fullscreenPopups[idx];
    this.idx = idx;
    if (focused_popup) {
      this.popup = focused_popup.popup;
      this.opened = focused_popup.opened;
      this.fixedElements = focused_popup.fixedElements;
      this.fixedScroll = focused_popup.fixedScroll;
    } else {
      this.popup = null;
      this.opened = null;
      this.fixedElements = null;
      this.fixedScroll = null;
    }
  }

  saveFullscreenPopup() {
    if (this.popup) {
      this.fullscreenPopups[this.idx] = {
        popup: this.popup,
        opened: this.opened,
        fixedElements: this.fixedElements,
        fixedScroll: this.fixedScroll
      };
    } else {
      delete this.fullscreenPopups[this.idx];
    }
  }

  open(id, url) {
    lockBodyScroll();
    return new Promise(resolve => {
      this.loadFullscreenPopup(id);

      this.closeImpl();
      this.opened = true;

      let html = document.querySelector('html');
      let body = document.querySelector('body');

      if (!this.idx) {
        html.dataset.cremaOrgStyle = html.style.cssText;
        html.style.overflow = 'hidden';

        // # body 사이즈가 window.innerHeight 사이즈 이하 일때 overflow를 hidden를 주면 스크롤이 위로 올라가는 문제가 있다.
        const height = body.clientHeight;
        const windowHeight = window.innerHeight;
        let bodyCss = { height: 'auto' };
        if (windowHeight === 0 || height > windowHeight)
          bodyCss.overflow = 'hidden';

        body.dataset.cremaOrgStyle = body.style.cssText;
        setStyle(body, bodyCss);
      }

      this.popup = document.createElement('iframe');
      this.popup.setAttribute('id', id);
      this.popup.setAttribute('allowTransparency', 'true');
      this.popup.setAttribute('frameborder', 0);

      if (browserDetect.isIos) this.openImplIos(id, body);
      else this.openImpl(id, body);

      const urlObject = new URL(url, window.location.origin);
      urlObject.searchParams.set('iframe_id', id);
      this.popup.setAttribute('src', urlObject.href);

      this.saveFullscreenPopup();

      if (this.modalHistoryEnabled()) {
        const onPopstate = () => {
          if (window.history.state?.cremaBottomSheet) return;

          window.removeEventListener('popstate', onPopstate);
          unLockBodyScroll();
          this.closeImpl();
          this.saveFullscreenPopup();
          if (this.enable_child && this.idx != 0) {
            while (!this.fullscreenPopups[this.idx]) this.idx -= 1;
            this.loadFullscreenPopupByIdx(this.idx);
            window.addEventListener('popstate', onPopstate);
          }
        };
        if (browserDetect.supportsPopState) {
          window.removeEventListener('popstate', onPopstate);
          window.history.pushState(
            { modal: this.idx },
            '',
            `#modal_${this.idx}`
          );
        }
        window.addEventListener('popstate', onPopstate);
      }

      this.bindMessageHandler(resolve);
    });
  }

  bindMessageHandler(resolve) {
    const messageHandler = e => {
      if (window.location.origin !== e.origin) return;

      const data = deparam(e.data);
      switch (data.act) {
        case 'open_fullscreen_popup': {
          window.removeEventListener('message', messageHandler);
          const decodedUrl = decodeURIComponent(data.url);
          this.windowService.openFullscreenPopup(decodedUrl);
          break;
        }
        case 'close_fullscreen_popup':
          this.close(data.iframe_id);
          if (!this.fullscreenPopups.filter(v => v).length)
            window.removeEventListener('message', messageHandler);
          resolve(decodeValue(data.response));
          break;
        case 'onload':
          this.popup.style.visibility = 'visible';
          break;
        case 'redirect_to': {
          const decodedUrl = decodeURIComponent(data.url);
          this.windowService.redirectTo(decodedUrl);
          break;
        }
        case 'keydown':
          this.postKeydown(data.keyCode);
          break;
        case 'alert':
          window.alert(data.msg);
          break;
      }
    };
    window.addEventListener('message', messageHandler);
  }

  openImplIos(_id, body) {
    const iosPopupContainer = this.idx
      ? $(`<div id='crema-ios-popup-container-${this.idx}'></div>`)
      : $("<div id='crema-ios-popup-container'></div>");

    setStyle(iosPopupContainer, {
      overflowY: 'scroll',
      overflowScrolling: 'touch',
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 9999999
    });
    body.append(iosPopupContainer);

    setStyle(this.popup, {
      width: '100%',
      height: '100%'
    });
    iosPopupContainer.append(this.popup);
  }

  openImpl(_id, _body) {
    let style = {
      width: '100%',
      height: '100%',
      zIndex: 9999999
    };

    // IE 의 경우 구버전 렌더 모드 시 position: fixed가 되지않고 chrome의 경우 윈도우에서 overflow: hidden 으로 scrolling lock이 되지 않아 분기해 처리한다.
    if (browserDetect.supportsFixedPosition) {
      style.position = 'fixed';
      style.top = 0;
      style.left = 0;
    } else {
      style.position = 'absolute';
      style.top = document.documentElement.scrollTop || document.body.scrollTop;
      style.left =
        document.documentElement.scrollLeft || document.body.scrollLeft;
    }
    setStyle(this.popup, style);
    document.querySelector('body').append(this.popup);
  }

  close(id) {
    this.loadFullscreenPopup(id);

    if (!this.opened) return;

    this.opened = false;
    this.closeImpl();
    unLockBodyScroll();

    if (this.modalHistoryEnabled() && browserDetect.supportsPopState)
      window.history.back();
  }

  closeImpl() {
    this.opened = false;
    if (this.fixedScroll) {
      window.removeEventListener('scroll', this.fixedScroll);
      this.fixedScroll = null;
    }

    if (this.popup) {
      this.popup.remove();
      if (this.idx)
        document
          .querySelector(`#crema-ios-popup-container-${this.idx}`)
          ?.remove();
      else document.querySelector('#crema-ios-popup-container')?.remove();

      if (!this.idx) {
        const html = document.querySelector('html');
        const orgStyle = html.dataset.cremaOrgStyle;
        if (orgStyle) setStyle(html, orgStyle);
        else html.removeAttribute('style');
        delete html.dataset.cremaOrgStyle;

        const body = document.querySelector('body');
        const bodyOrgStyle = body.dataset.cremaOrgStyle;
        if (bodyOrgStyle) setStyle(body, bodyOrgStyle);
        else body.removeAttribute('style');
        delete body.dataset.cremaOrgStyle;
      }
      this.popup = null;
    }

    if (this.keydown) {
      document.removeEventListener('keydown', this.keydown);
      this.keydown = null;
    }
    this.saveFullscreenPopup();
  }

  postKeydown(keyCode) {
    window.postMessage(
      { act: 'keydown', keyCode: keyCode },
      window.location.protocol +
        '//' +
        window.location.host +
        this.popup.attr('src'),
      this.popup.get(0).contentWindow
    );
  }

  modalHistoryEnabled() {
    return browserDetect.isMobile && browserDetect.supportsHistory;
  }
}

export default FullscreenPopup;
